import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { userApi } from '../../services/api';
import toast from 'react-hot-toast';

const Settings: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);

  const handleProfileUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    
    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());
    
    try {
      await userApi.updateMe(data);
      await refreshUser();
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-gray-600">Manage your account settings and preferences</p>
      </div>

      <div className="flex space-x-1 rounded-xl bg-gray-100 p-1 max-w-md">
        {['profile', 'company', 'notifications', 'security'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`w-full rounded-lg py-2.5 text-sm font-medium leading-5 capitalize ${
              activeTab === tab
                ? 'bg-white text-brand-700 shadow'
                : 'text-gray-600 hover:bg-white/[0.5] hover:text-gray-800'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'profile' && (
        <div className="bg-white shadow rounded-lg p-6">
          <form onSubmit={handleProfileUpdate} className="space-y-6 max-w-lg">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  name="firstName"
                  type="text"
                  defaultValue={user?.firstName}
                  className="input"
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  name="lastName"
                  type="text"
                  defaultValue={user?.lastName}
                  className="input"
                />
              </div>
            </div>
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="input bg-gray-50"
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>
            <div>
              <label className="label">Phone</label>
              <input
                name="phone"
                type="tel"
                defaultValue={user?.phone}
                className="input"
              />
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'company' && (
        <div className="bg-white shadow rounded-lg p-6">
          <form onSubmit={handleProfileUpdate} className="space-y-6 max-w-lg">
            <div>
              <label className="label">Company Name</label>
              <input
                name="companyName"
                type="text"
                defaultValue={user?.companyName}
                className="input"
              />
            </div>
            <div>
              <label className="label">Industry</label>
              <input
                name="industry"
                type="text"
                defaultValue={user?.industry}
                className="input"
              />
            </div>
            <div>
              <label className="label">Website</label>
              <input
                name="website"
                type="url"
                defaultValue={user?.website}
                className="input"
              />
            </div>
            <div>
              <label className="label">CAGE Code</label>
              <input
                name="cageCode"
                type="text"
                defaultValue={user?.cageCode}
                className="input"
              />
            </div>
            <div>
              <label className="label">UEI (SAM)</label>
              <input
                name="ueiSam"
                type="text"
                defaultValue={user?.ueiSam}
                className="input"
              />
            </div>
            <button type="submit" disabled={isLoading} className="btn-primary">
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-4 max-w-lg">
            {[
              { id: 'email_alerts', label: 'Email Alerts', desc: 'Get notified about new opportunities matching your profile' },
              { id: 'deadline_reminders', label: 'Deadline Reminders', desc: 'Receive reminders before proposal deadlines' },
              { id: 'team_updates', label: 'Team Updates', desc: 'Notifications about team member activities' },
              { id: 'marketing', label: 'Marketing Emails', desc: 'Product updates and GovCon tips' },
            ].map((item) => (
              <div key={item.id} className="flex items-start">
                <div className="flex h-5 items-center">
                  <input
                    id={item.id}
                    type="checkbox"
                    defaultChecked
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor={item.id} className="font-medium text-gray-700">
                    {item.label}
                  </label>
                  <p className="text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="space-y-6 max-w-lg">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h3>
              <p className="text-sm text-gray-500 mt-1">
                Add an extra layer of security to your account
              </p>
              <div className="mt-4">
                <button className="btn-secondary">
                  {user?.twoFactorEnabled ? 'Disable 2FA' : 'Enable 2FA'}
                </button>
              </div>
            </div>
            <hr />
            <div>
              <h3 className="text-lg font-medium text-gray-900">Change Password</h3>
              <p className="text-sm text-gray-500 mt-1">
                Update your password regularly to keep your account secure
              </p>
              <div className="mt-4 space-y-4">
                <input type="password" placeholder="Current password" className="input" />
                <input type="password" placeholder="New password" className="input" />
                <input type="password" placeholder="Confirm new password" className="input" />
                <button className="btn-primary">Update Password</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
